<template>
	<div class="container py-4">
		<div class="d-flex align-items-center mb-4">
			<RouterLink
				class="btn btn-outline-secondary me-3"
				to="/_/inventory/catalog"
				title="Back to the pesticide catalog"
			>
				<i class="bi bi-arrow-left"></i>
			</RouterLink>
			<div class="flex-grow-1">
				<h1 class="mb-0">{{ product?.name ?? "Pesticide Product" }}</h1>
				<p v-if="product" class="text-muted mb-0 font-monospace">
					{{ product.product_id }}
				</p>
			</div>
			<ButtonLoading
				v-if="product"
				class="ms-3"
				:text="
					product.in_district
						? 'Remove from my district list'
						: 'Add to my district list'
				"
				:icon="product.in_district ? 'bi bi-dash-lg' : 'bi bi-plus-lg'"
				:variant="product.in_district ? 'outline-danger' : 'primary'"
				:loading="pending"
				@click="toggleDistrictList"
			/>
		</div>

		<div v-if="error" class="alert alert-danger" role="alert">{{ error }}</div>

		<div v-if="loading" class="text-center py-5">
			<div class="spinner-border text-primary" role="status">
				<span class="visually-hidden">Loading…</span>
			</div>
		</div>

		<template v-else-if="product">
			<div class="card shadow-sm mb-4">
				<div class="card-body">
					<div class="d-flex justify-content-between align-items-start mb-4">
						<div>
							<span class="badge rounded-pill bg-primary fs-5">{{
								product.category
							}}</span>
						</div>
						<div class="text-nowrap">
							<a
								v-if="product.label_document_url"
								class="btn btn-sm btn-primary me-1"
								:href="product.label_document_url"
								target="_blank"
								rel="noopener"
							>
								<i class="bi bi-file-earmark-pdf me-1"></i>Label
							</a>
							<a
								v-if="product.sds_document_url"
								class="btn btn-sm btn-outline-secondary"
								:href="product.sds_document_url"
								target="_blank"
								rel="noopener"
							>
								<i class="bi bi-file-earmark-pdf me-1"></i>SDS
							</a>
						</div>
					</div>

					<div class="row g-3">
						<div class="col-md-6 col-lg-4">
							<div class="info-label">Formulation</div>
							<div>{{ product.formulation || "—" }}</div>
							<div v-if="product.formulation_source" class="source-note">
								Source: {{ product.formulation_source }}
							</div>
							<div
								v-if="product.formulation_source_note"
								class="source-evidence"
							>
								{{ product.formulation_source_note }}
							</div>
						</div>
						<div class="col-md-6 col-lg-4">
							<div class="info-label">Manufacturer</div>
							<div>{{ product.manufacturer || "—" }}</div>
							<div v-if="product.manufacturer_source" class="source-note">
								Source: {{ product.manufacturer_source }}
							</div>
							<div
								v-if="product.manufacturer_source_note"
								class="source-evidence"
							>
								{{ product.manufacturer_source_note }}
							</div>
						</div>
						<div class="col-md-6 col-lg-4">
							<div class="info-label">EPA Registration</div>
							<div>{{ product.epa_registration || "—" }}</div>
							<div v-if="product.epa_source" class="source-note">
								Source: {{ product.epa_source }}
							</div>
						</div>
						<!-- The brand hop: the packaged product on the shelf. The
						     registration is the legal parent; the brand is what the
						     district actually buys. -->
						<div class="col-md-6 col-lg-4">
							<div class="info-label">Brand</div>
							<div>
								{{ product.registry_brand_name || "—" }}
								<span
									v-if="
										product.registry_brand_id !== null &&
										product.registry_brand_active === false
									"
									class="badge text-bg-secondary ms-1"
									title="The EPA lists this brand as inactive"
									>Inactive</span
								>
							</div>
							<div
								v-if="
									product.registry_brand_id !== null &&
									product.registry_brand_sds_present === false
								"
								class="small text-danger mt-1"
							>
								<i class="bi bi-exclamation-triangle me-1"></i>No SDS filed for
								this brand
							</div>
							<a
								v-else-if="product.registry_brand_sds_document_url"
								class="small"
								:href="product.registry_brand_sds_document_url"
								target="_blank"
								rel="noopener"
							>
								<i class="bi bi-file-earmark-pdf me-1"></i>Brand SDS
							</a>
						</div>
						<div class="col-md-6 col-lg-4">
							<div class="info-label">Active Ingredients</div>
							<div>{{ product.active_ingredients || "—" }}</div>
							<div v-if="product.ai_composition_source" class="source-note">
								Source: {{ product.ai_composition_source }}
							</div>
						</div>
						<div class="col-md-6 col-lg-4">
							<div class="info-label">Target Life Stages</div>
							<LifeStageCircles :active="activeStages" />
						</div>
						<div class="col-md-6 col-lg-4">
							<div class="info-label">Manufacturer Label Page</div>
							<div>
								<a
									v-if="product.label_url"
									:href="product.label_url"
									target="_blank"
									rel="noopener"
								>
									{{ product.label_url }}
								</a>
								<span v-else>—</span>
							</div>
							<div v-if="product.label_source" class="source-note">
								Source: {{ product.label_source }}
							</div>
						</div>
						<div class="col-md-6 col-lg-4">
							<div class="info-label">Added</div>
							<div>
								<TimeRelativeWithTooltip :time="product.created" />
							</div>
						</div>
						<div v-if="product.retired" class="col-md-6 col-lg-4">
							<div class="info-label">Retired</div>
							<div>
								<TimeRelativeWithTooltip :time="product.retired" />
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Where a district buys it. Coverage is stated directly against
			     districts, and a district can record the distributor it buys from. -->
			<div
				v-if="product.registry_brand_id !== null"
				class="card shadow-sm mb-4"
			>
				<div class="card-body">
					<h2 class="h4 mb-3">Where to buy</h2>
					<WhereToBuy
						:rows="product.where_to_buy ?? []"
						:brand-id="product.registry_brand_id"
						:audit="isRoot"
						:can-record-purchase="true"
						@changed="load(true)"
					/>
				</div>
			</div>

			<div class="card shadow-sm">
				<div class="card-body">
					<h2 class="h4 mb-3">Application Rates</h2>
					<div v-if="product.rates.length === 0" class="text-muted">
						No application rates recorded for this product.
					</div>
					<div v-else class="table-responsive">
						<table class="table table-striped align-middle">
							<thead class="table-light">
								<tr>
									<th>Scenario</th>
									<th>Target Life Stages</th>
									<th>Rate</th>
									<th>Residual (days)</th>
									<th>Notes</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="rate in product.rates" :key="rate.id">
									<td>{{ rate.scenario_description || "—" }}</td>
									<td>
										<LifeStageCircles
											:active="activeLifeStages(rate.target_life_stages)"
										/>
									</td>
									<td>
										<div>{{ formatRateRange([rate]) || "—" }}</div>
										<div
											v-if="formatPreferredRate(rate)"
											class="text-muted small"
										>
											Preferred: {{ formatPreferredRate(rate) }}
										</div>
									</td>
									<td>{{ formatResidualDays([rate]) || "—" }}</td>
									<td class="text-muted small">
										{{ rate.calculation_notes || "—" }}
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</template>

		<ToastNotification
			:title="toastTitle"
			:message="toastMessage"
			:show="toastShow"
			@update:show="toastShow = $event"
		/>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";

import { apiClient } from "@/client";
import ButtonLoading from "@/components/common/ButtonLoading.vue";
import LifeStageCircles from "@/components/pesticide/LifeStageCircles.vue";
import WhereToBuy from "@/components/pesticide/WhereToBuy.vue";
import TimeRelativeWithTooltip from "@/components/TimeRelativeWithTooltip.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import { useStoreProduct } from "@/store/product";
import { useSessionStore } from "@/store/session";
import type { PesticideCatalogProduct } from "@/type/api";
import {
	activeLifeStages,
	formatPreferredRate,
	formatRateRange,
	formatResidualDays,
} from "@/view/configuration/pesticideCatalog";

const route = useRoute();
const storeProduct = useStoreProduct();
const sessionStore = useSessionStore();

// A root reading the district page still gets the curator's provenance; the
// server decides what to send, this only renders it.
const isRoot = computed(() => sessionStore.self?.role === "root");

const product = ref<PesticideCatalogProduct | null>(null);
const loading = ref(false);
const error = ref("");
const pending = ref(false);

const activeStages = computed(() =>
	product.value
		? activeLifeStages(product.value.target_life_stages)
		: new Set<string>(),
);

const toastTitle = ref("");
const toastMessage = ref("");
const toastShow = ref(false);

function showNotification(title: string, message: string) {
	toastTitle.value = title;
	toastMessage.value = message;
	toastShow.value = true;
}

async function load(silent = false) {
	if (!silent) loading.value = true;
	error.value = "";
	try {
		product.value = await apiClient.JSONGet<PesticideCatalogProduct>(
			`/api/pesticide-catalog/${route.params.id}`,
		);
	} catch (e: any) {
		error.value = e?.message || "Failed to load this pesticide product.";
		showNotification("Failed to load pesticide product", error.value);
	} finally {
		if (!silent) loading.value = false;
	}
}

// The page is otherwise read-only reference material: this one action adds the
// entry to the district's own list or retires the district product that holds
// it. Both outcomes are re-read from the server, so the button always reflects
// the ownership the catalog reports rather than a local guess.
async function toggleDistrictList() {
	if (!product.value || pending.value) return;
	pending.value = true;
	const entry = product.value;
	try {
		if (entry.in_district && entry.district_product_id !== null) {
			await storeProduct.retire(entry.district_product_id);
			await load(true);
			showNotification(
				"Removed from your district list",
				`"${entry.name}" is no longer in your district's list.`,
			);
		} else {
			const created = await storeProduct.createFromCatalog(entry.product_id);
			await load(true);
			showNotification(
				"Added to your district list",
				`"${created.name}" is now in your district's list.`,
			);
		}
	} catch (e: any) {
		showNotification(
			entry.in_district ? "Failed to remove product" : "Failed to add product",
			e?.message || "Please try again.",
		);
	} finally {
		pending.value = false;
	}
}

onMounted(load);
</script>

<style scoped>
.info-label {
	font-size: 0.8rem;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.03em;
	color: #6c757d;
	margin-bottom: 0.25rem;
}

.source-note {
	font-size: 0.8rem;
	color: #6c757d;
}

/* The evidence behind a short source value: the label sentence or the naming
   convention applied. Kept small and muted so the short origin stays legible. */
.source-evidence {
	font-size: 0.75rem;
	color: #adb5bd;
	margin-top: 0.15rem;
}
</style>
